/* eslint-disable max-classes-per-file */
import { EventEmitter } from '@angular/core';
import moment from 'moment';
import { LuceneExpression, term, RangeExpression, FieldExpression, NodeExpression } from 'lucene';

import { safe } from '../utils/safe';

import {
	DATE_FORMAT_PATTERN,
	DATE_FORMAT_PATTERN_ZONE,
	QUARTER_FORMAT_PATTERN,
	WEEK_FORMAT_PATTERN
} from './filter-formats';
import {
	collectFieldValues,
	deduplicate,
	ensureRangeValue,
	ensureString,
	extractConditionsByFields,
	extractCombinedConditionsByFields,
	normalizeDateBoundary,
	parseBooleanFilterSelections,
	parseDocumentDropdownValues,
	parseDocumentLastConnectionDateSelections,
	parseDocumentSuggestiveListIds,
	parseNumericRanges,
	parseShipmentOrderNumbers,
	resolveDateFormat,
	shouldAdjustUpperBound,
	parseOrderNumberValue,
	escapeRegExp,
	isNonEmptyString
} from './datacenter-lucene.utils';

function hasWildcardOperator(value: string) {
	return /[*?]/.test(value);
}

function escapeTextPreservingWildcards(value: string) {
	let escapedValue = '';
	let literalValue = '';
	const appendLiteralValue = () => {
		escapedValue += term.escape(literalValue);
		literalValue = '';
	};
	let index = 0;
	while (index < value.length) {
		const character = value[index];
		const nextCharacter = value[index + 1];
		if (character === '\\' && (nextCharacter === '*' || nextCharacter === '?')) {
			appendLiteralValue();
			escapedValue += `\\${ nextCharacter }`;
			index += 2;
		} else if (character === '*' || character === '?') {
			appendLiteralValue();
			escapedValue += character;
			index += 1;
		} else {
			literalValue += character;
			index += 1;
		}
	}
	appendLiteralValue();
	return escapedValue;
}

export type FilterType = DatacenterFreeTextFilter | DatacenterDateFilter | SuggestiveListFilter | OrderNumberFilter |
	DatacenterNumericFilter | DatacenterBooleanFilter | DropdownFilter | DocumentOrderNumberFilter |
	DocumentDateFilter | DocumentLastConnectionDateFilter |
	ShipmentOrderNumberFilter | GlobalSearchFilter | DocumentMillDropdownFilter | SuggestiveListWithIdFilter
	| DocumentSuggestiveListFilterWithId;

export interface DatacenterFilterType {
	id: string;
	values: string[] | DatacenterDateFilterType[] |
		DatacenterCodeTranslationFilterType[] | DatacenterOrderNumberFilterType[] | DatacenterDateNumericType[] |
		DatacenterBooleanFilterType[][] | DatacenterDocumentOrderNumberFilterType[] |
		DocumentDateFilterType[] | DocumentLastConnectionDateFilterType[][];
}

export interface DatacenterDateNumericType {
	from: string;
	to: string;
}

interface DatacenterDateFilterType {
	from: Date | '';
	to: Date | '';
}

interface DatacenterCodeWithIdTranslationFilterType {
	code: string | undefined;
	translation: string | undefined;
}

export interface DocumentDateFilterType {
	from: Date | '';
	to: Date | '';
	entityName?: string;
}

export interface DatacenterCodeTranslationFilterType {
	code?: string | undefined;
	translation?: string | undefined;
	extraField?: string | undefined;
}

interface DatacenterDocumentCodeTranslationFilterType {
	code?: string | undefined;
	translation?: string | undefined;
	entityName?: string | undefined;
}

export interface DocumentCodeWithIdFilterType {
	id: string | undefined;
	code: string | undefined;
	entityName?: string | undefined;
}

interface DatacenterOrderNumberFilterType {
	reference?: string | undefined;
	item?: string | undefined;
}

export interface DatacenterDocumentOrderNumberFilterType {
	reference?: string | undefined;
	item?: string | undefined;
	entityReferenceName?: string | undefined;
}

export interface DatacenterBooleanFilterType {
	apiName: string;
	selected: boolean;
	value?: string | undefined;
}

export interface DocumentLastConnectionDateFilterType {
	apiName: string;
	selected: boolean;
	value: string;
}

abstract class DatacenterViewFilter<T> {
	private exactTextMatching = false;

	abstract readonly filterType: string;

	protected constructor(
		public filterId: string,
		public label: string,
		protected values: T[] = []
	) {}

	addValue(value: T) {
		this.values = [...this.values, value];
	}

	addValues(values: T[]) {
		this.values = [...values];
	}

	getValues() {
		return this.values;
	}

	valueSize() {
		return this.values.length;
	}

	enableExactTextMatching() {
		this.exactTextMatching = true;
	}

	protected isExactTextMatchingEnabled() {
		return this.exactTextMatching;
	}

	abstract addEmptyValue(): void;
	abstract getFilters(): { id: string; values: T[] };

	abstract getLuceneExpression(): LuceneExpression | undefined;
	abstract fromLuceneExpression(expression: LuceneExpression): { id: string; values: T[] };
}

export class GlobalSearchFilter extends DatacenterViewFilter<string> {
	readonly filterType = 'GlobalSearch';
	constructor(
		public override filterId: string,
		public override label: string,
		public placeholder: string,
		public apiNames: string[],
		protected override values: string[] = [],
	) {
		super(filterId, label, values);
	}

	addEmptyValue() {
		super.addValue('');
	}

	getFilters() {
		return { id: this.filterId, values: this.values };
	}

	getLuceneExpression() {
		let result: NodeExpression | LuceneExpression | undefined;
		const searchValue = this.values
			.map(value => typeof value === 'string' ? value.trim() : '')
			.find(candidate => candidate.length > 0) ?? '';
		if (!searchValue) {
			return undefined;
		}
		for (const apiName of this.apiNames) {
			result = result
				? {
					left: result,
					operator: 'OR',
					parenthesized: false,
					right: this.createFieldExpression(apiName, searchValue)
				}
				: this.createFieldExpression(apiName, searchValue);
		}
		return result;
	}

	fromLuceneExpression(expression: LuceneExpression): { id: string; values: string[] } {
		return { id: this.filterId, values: this.collectImplicitTerms(expression) };
	}

	buildOrderNumberFilter(textSearch: string, orderField: string, itemField: string) {
		const defaultCondition: FieldExpression = {
			field: orderField,
			term: `*${ term.escape(textSearch) }*`,
			quoted: false
		};
		if (textSearch.includes('/')) {
			const orderNumber = textSearch.slice(0, textSearch.lastIndexOf('/'));
			const itemNumber = textSearch.slice(textSearch.lastIndexOf('/') + 1);
			const isNum = /^\d+$/.test(itemNumber);
			if (isNum && orderNumber.length > 0) {
				const orderNumberField: FieldExpression = {
					field: orderField,
					term: `*${ term.escape(orderNumber) }*`,
					quoted: false
				};
				const orderItemNumberField: FieldExpression = {
					field: itemField,
					term: itemNumber,
					quoted: itemNumber === ''
				};
				const orderNumberNode: NodeExpression = {
					left: orderNumberField,
					operator: 'AND',
					parenthesized: true,
					right: orderItemNumberField
				};
				const finalOrderNode: NodeExpression = {
					left: orderNumberNode,
					operator: 'OR',
					parenthesized: true,
					right: defaultCondition
				};
				return finalOrderNode;
			}
			return defaultCondition;
		}
		return defaultCondition;
	}

	private buildOrExpression(value: string, refernceField: string, itemField: string) {
		const conditionOnDocumnetLine = this.buildOrderNumberFilter(value, refernceField, itemField);
		const conditionOnDocument = this.buildOrderNumberFilter(value, refernceField.split('.')[1], itemField);
		return {
			left: conditionOnDocumnetLine,
			operator: 'OR',
			parenthesized: true,
			right: conditionOnDocument
		};
	}

	private createFieldExpression(apiName: string, value: string) {
		const orderNumberMappings: Record<string, [string, string]> = {
			supplierOrderNumber: ['supplierOrderNumber', 'supplierOrderItemNumber'],
			customerOrderNumber: ['customerOrderNumber', 'customerOrderItemNumber'],
			millOrderNumber: ['millOrderNumber', 'millOrderItemNumber'],
			supplierRFQNumber: ['supplierRFQNumber', 'supplierRFQItemNumber'],
			ebusinessOrderNumber: ['ebusinessOrderNumber', 'ebusinessOrderItemNumber'],
		};

		const documentDatacenterorderNumberMapping: Record<string, [string, string]> = {
			supplierOrderReference: [
				'documentLines.supplierOrderReference',
				'documentLines.supplierOrderReferenceItem'
			],
			customerOrderReference: [
				'documentLines.customerOrderReference',
				'documentLines.customerOrderItemReference'
			]
		};

		if (apiName in orderNumberMappings) {
			const [orderNumber, orderItemNumber] = orderNumberMappings[apiName];
			return this.buildOrderNumberFilter(value, orderNumber, orderItemNumber);
		}

		if (apiName in documentDatacenterorderNumberMapping) {
			const [reference, item] = documentDatacenterorderNumberMapping[apiName];
			return this.buildOrExpression(value, reference, item);
		}

		return {
			field: apiName,
			term: `*${ term.escape(value.trim()) }*`,
			quoted: false
		};
	}

	private collectImplicitTerms(expression: LuceneExpression): string[] {
		const values: string[] = [];
		const seen = new Set<string>();
		const stack: unknown[] = [expression];
		while (stack.length > 0) {
			const current = stack.pop();
			if (!current || typeof current !== 'object') {
				continue;
			}
			const candidate = <{ field?: unknown; term?: unknown; left?: unknown; right?: unknown }> current;
			if (typeof candidate.field === 'string'
				&& candidate.field === '<implicit>'
				&& typeof candidate.term === 'string') {
				const normalized = this.normalizeSearchValue(candidate.term);
				if (normalized && !seen.has(normalized)) {
					seen.add(normalized);
					values.push(normalized);
				}
			}
			if (candidate.left !== undefined) {
				stack.push(candidate.left);
			}
			if (candidate.right !== undefined) {
				stack.push(candidate.right);
			}
		}
		return values;
	}

	private normalizeSearchValue(rawValue?: string) {
		if (!rawValue) {
			return '';
		}
		const unescaped = term.unescape(rawValue).trim();
		if (unescaped.length >= 2 && unescaped.startsWith('*') && unescaped.endsWith('*')) {
			return unescaped.slice(1, -1).trim();
		}
		return unescaped;
	}
}

export class DatacenterFreeTextFilter extends DatacenterViewFilter<string> {
	readonly filterType = 'FreeText';

	constructor(
		public override filterId: string,
		public override label: string,
		public placeholder: string,
		public apiName: string,
		protected override values: string[] = []
	) {
		super(filterId, label, values);
	}

	addEmptyValue() {
		super.addValue('');
	}

	getFilters() {
		return { id: this.filterId, values: this.values.filter(Boolean) };
	}

	getLuceneExpression() {
		let result: LuceneExpression | undefined;
		for (const filterValues of this.values.filter(Boolean)) {
			for (const filterValue of filterValues.trim().split(';')) {
				result = result
					? {
						left: result,
						operator: 'OR',
						parenthesized: false,
						right: this.createFieldExpression(filterValue)
					}
					: this.createFieldExpression(filterValue);
			}
		}

		return result;
	}

	fromLuceneExpression(expression: LuceneExpression): { id: string; values: string[] } {
		const fieldValues = collectFieldValues(expression, [this.apiName]);
		const values = deduplicate(
			(fieldValues.get(this.apiName) ?? []).map(value => ensureString(value)).filter(isNonEmptyString),
			value => value.toLowerCase()
		);
		return { id: this.filterId, values };
	}

	private scaleToFixed(value: string, scale = 1000, decimals = 6): string {
		const n = Number(String(value).replace(/,/g, '').trim());
		return Number.isFinite(n) ? (n * scale).toFixed(decimals) : value;
	}

	private createFieldExpression(filterValue: string) {
		if (this.apiName === 'lengthDimensionConcat') {
			const value = this.isExactTextMatchingEnabled() && hasWildcardOperator(filterValue)
				? filterValue.trim() : String(Number(filterValue));
			return <const> {
				field: this.apiName,
				term: this.isExactTextMatchingEnabled()
					? escapeTextPreservingWildcards(value)
					: `*${ term.escape(String(value).trim()) }*`,
				quoted: false
			};
		}
		if (this.apiName === 'producedLengthDimensionConcat') {
			const scaled = this.isExactTextMatchingEnabled() && hasWildcardOperator(filterValue)
				? filterValue.trim() : this.scaleToFixed(filterValue);
			const producedExpr = <const>{
				field: this.apiName,
				term: this.isExactTextMatchingEnabled()
					? escapeTextPreservingWildcards(scaled) : `*${ scaled }*`,
				quoted: false
			};
			// right side: form:04
			const formExpr = <const>{
				field: 'form',
				term: '04',
				quoted: false
			};
			// Keep the form restriction grouped with produced length when composed.
			return <const>{
				left: producedExpr,
				operator: 'AND',
				right: formExpr,
				parenthesized: true
			};
		}
		return {
			field: this.apiName,
			term: this.filterId === 'countryCode'
				? filterValue.trim()
				: this.isExactTextMatchingEnabled()
					? escapeTextPreservingWildcards(filterValue.trim())
					: `*${ term.escape(filterValue.trim()) }*`,
			quoted: false
		};
	}
}

export class DatacenterDateFilter extends DatacenterViewFilter<DatacenterDateFilterType> {
	readonly filterType = 'Date';

	constructor(
		public override filterId: string,
		public override label: string,
		public placeholderFrom: string,
		public placeholderTo: string,
		public apiName: string,
		public filterDateType: string,
		protected override values: DatacenterDateFilterType[] = []
	) {
		super(filterId, label, values);
	}

	addEmptyValue() {
		super.addValue({ from: '', to: '' });
	}

	getFilters() {
		return { id: this.filterId, values: this.values.filter(value => value.to || value.from) };
	}

	getLuceneExpression() {
		let result: LuceneExpression | undefined;
		for (const filterValue of this.values.filter(value => value.to || value.from)) {
			result = result
				? {
					left: result,
					operator: 'OR',
					parenthesized: false,
					right: this.createRangeExpression(filterValue)
				}
				: this.createRangeExpression(filterValue);
		}

		return result;
	}

	fromLuceneExpression(expression: LuceneExpression): { id: string; values: DatacenterDateFilterType[] } {
		const conditions = extractConditionsByFields(expression, [this.apiName]);
		const format = resolveDateFormat(this.filterDateType);
		const adjustUpperBound = shouldAdjustUpperBound(this.filterDateType);
		const values: DatacenterDateFilterType[] = [];
		for (const condition of conditions) {
			const range = ensureRangeValue(condition[this.apiName]);
			if (!range) {
				continue;
			}
			const fromBoundary = normalizeDateBoundary(range.from, format);
			const toBoundary = normalizeDateBoundary(range.to, format, adjustUpperBound);
			values.push({
				from: fromBoundary instanceof Date ? fromBoundary : '',
				to: toBoundary instanceof Date ? toBoundary : ''
			});
		}
		return { id: this.filterId, values };
	}

	private createRangeExpression(filterValue: DatacenterDateFilterType) {
		const filtersValue = this.getFilterValue(filterValue);
		return <const> {
			field: this.apiName,
			...filtersValue,
			inclusive: 'both'
		};
	}

	private getFilterValue(filterValue: DatacenterDateFilterType) {
		switch (this.filterDateType) {
		case 'week':
			return {
				term_min: filterValue.from ? moment(filterValue.from).format(WEEK_FORMAT_PATTERN) : '*',
				term_max: filterValue.to ? moment(filterValue.to).format(WEEK_FORMAT_PATTERN) : '*',
			};
		case 'quarter':
			return {
				term_min: filterValue.from ? moment(filterValue.from).format(QUARTER_FORMAT_PATTERN) : '*',
				term_max: filterValue.to ? moment(filterValue.to).format(QUARTER_FORMAT_PATTERN) : '*',
			};
		default:
			return {
				term_min: filterValue.from ? moment(filterValue.from).format(DATE_FORMAT_PATTERN) : '*',
				term_max: filterValue.to ? moment(filterValue.to).format(DATE_FORMAT_PATTERN) : '*',
			};
		}
	}
}

export class DocumentDateFilter extends DatacenterViewFilter<DocumentDateFilterType> {
	readonly filterType = 'DocumentDate';

	constructor(
		public override filterId: string,
		public override label: string,
		public placeholderFrom: string,
		public placeholderTo: string,
		public apiName: string,
		public entityName: string,
		public filterDateType: string,
		protected override values: DocumentDateFilterType[] = []
	) {
		super(filterId, label, values);
	}

	addEmptyValue() {
		super.addValue({ from: '', to: '', entityName: this.entityName });
	}

	getFilters() {
		return { id: this.filterId, values: this.values.filter(value => value.to || value.from) };
	}

	getLuceneExpression() {
		let result: LuceneExpression | undefined;
		for (const filterValue of this.values.filter(value => value.to || value.from)) {
			result = result
				? {
					left: result,
					operator: 'OR',
					parenthesized: false,
					right: this.createDateRangeExpression(filterValue)
				}
				: this.createDateRangeExpression(filterValue);
		}

		return result;
	}

	fromLuceneExpression(expression: LuceneExpression): { id: string; values: DocumentDateFilterType[] } {
		const baseField = this.apiName;
		const entityField = this.entityName ? `${ this.entityName }.${ this.apiName }` : undefined;
		const targetFields = entityField ? [baseField, entityField] : [baseField];
		const conditions = extractConditionsByFields(expression, targetFields);
		const format = resolveDateFormat(this.filterDateType);
		const adjustUpperBound = shouldAdjustUpperBound(this.filterDateType);
		const grouped = new Map<string, { range: { from: string; to: string }; includeEntity: boolean }>();
		for (const condition of conditions) {
			for (const [field, rawValue] of Object.entries(condition)) {
				const range = ensureRangeValue(rawValue);
				if (!range) {
					continue;
				}
				const key = `${ range.from }|${ range.to }`;
				const existing = grouped.get(key) ?? { range, includeEntity: false };
				if (entityField && field === entityField) {
					existing.includeEntity = true;
				}
				grouped.set(key, existing);
			}
		}
		const values: DocumentDateFilterType[] = [];
		for (const { range, includeEntity } of grouped.values()) {
			const fromBoundary = normalizeDateBoundary(range.from, format);
			const toBoundary = normalizeDateBoundary(range.to, format, adjustUpperBound);
			values.push({
				from: fromBoundary instanceof Date ? fromBoundary : '',
				to: toBoundary instanceof Date ? toBoundary : '',
				entityName: includeEntity ? this.entityName : undefined
			});
		}
		return { id: this.filterId, values };
	}

	private createDateRangeExpression(filterValue: DocumentDateFilterType) {
		let documentLineExp: RangeExpression | undefined;
		const filtersValue = this.getFilterValue(filterValue);
		const documentExp = <const> {
			field: this.apiName,
			...filtersValue,
			inclusive: 'both'
		};

		const shouldCreateEntityField =
            Boolean(filterValue.entityName);

		if (shouldCreateEntityField) {
			if (filterValue.entityName) {
				documentLineExp = <const>{
					field: `${ this.entityName }.${ this.apiName }`,
					...filtersValue,
					inclusive: 'both'
				};
			}

			if (documentLineExp !== undefined) {
				return {
					left: documentExp,
					operator: 'OR',
					parenthesized: true,
					right: documentLineExp
				};
			}
		}
		return documentExp;
	}

	private getFilterValue(filterValue: DocumentDateFilterType) {
		switch (this.filterDateType) {
		case 'week':
			return {
				term_min: filterValue.from ? moment(filterValue.from).format(WEEK_FORMAT_PATTERN) : '*',
				term_max: filterValue.to ? moment(filterValue.to).format(WEEK_FORMAT_PATTERN) : '*',
			};
		case 'quarter':
			return {
				term_min: filterValue.from ? moment(filterValue.from).format(QUARTER_FORMAT_PATTERN) : '*',
				term_max: filterValue.to ? moment(filterValue.to).format(QUARTER_FORMAT_PATTERN) : '*',
			};
		default:
			return {
				term_min: filterValue.from ? moment(filterValue.from).format(DATE_FORMAT_PATTERN_ZONE) : '*',
				term_max: filterValue.to ? moment(filterValue.to).format(DATE_FORMAT_PATTERN_ZONE) : '*',
			};
		}
	}
}

export class SuggestiveListFilter extends DatacenterViewFilter<DatacenterCodeTranslationFilterType> {
	readonly filterType = 'SuggestiveList';

	constructor(
		public override filterId: string,
		public override label: string,
		public placeholder: string,
		public apiNameCode: string | undefined,
		public apiNameTranslation: string,
		public requestApi: FilterApiRequest,
		protected override values: { code?: string; translation?: string }[] = [],
		public requestedFields?: string[],
	) {
		super(filterId, label, values);
	}

	addEmptyValue() {
		super.addValue({ code: undefined, translation: undefined });
	}

	getFilters() {
		const selectableValues = this.values.filter(value => Boolean(value.translation) || Boolean(value.code));
		return { id: this.filterId, values: selectableValues };
	}

	getLuceneExpression() {
		let result: LuceneExpression | undefined;
		for (const filterValue of this.values.filter(value => Boolean(value.translation) || Boolean(value.code))) {
			const expression = this.createFieldExpression(filterValue.translation, filterValue.code);
			if (!expression) {
				continue;
			}
			result = result
				? {
					left: result,
					operator: 'OR',
					parenthesized: false,
					right: expression
				}
				: expression;
		}
		return result;
	}

	fromLuceneExpression(expression: LuceneExpression): {
		id: string;
		values: DatacenterCodeTranslationFilterType[];
	} {
		const targetFields = [this.apiNameTranslation];
		if (this.apiNameCode) {
			targetFields.push(this.apiNameCode);
		}
		const conditions = extractConditionsByFields(expression, targetFields);
		const valuesMap = new Map<string, DatacenterCodeTranslationFilterType>();
		for (const condition of conditions) {
			for (const [field, rawValue] of Object.entries(condition)) {
				const value = ensureString(rawValue);
				if (!value) {
					continue;
				}
				const key = field === this.apiNameCode ? `code:${ value }` : `translation:${ value }`;
				const current = valuesMap.get(key) ?? { code: undefined, translation: undefined };
				if (field === this.apiNameCode) {
					current.code = value;
				} else {
					current.translation = value;
				}
				valuesMap.set(key, current);
			}
		}
		return { id: this.filterId, values: [...valuesMap.values()] };
	}

	private createFieldExpression(translation: string | undefined, code?: string) {
		if (!code && !translation) {
			return undefined;
		}
		return {
			field: code && this.apiNameCode ? this.apiNameCode : this.apiNameTranslation,
			term: code && this.apiNameCode ? code : `*${ term.escape(translation ?? '') }*`,
			quoted: false
		};
	}
}

export class SuggestiveListWithIdFilter extends DatacenterViewFilter<DatacenterCodeWithIdTranslationFilterType> {
	readonly filterType = 'SuggestiveListWithId';

	constructor(
		public override filterId: string,
		public override label: string,
		public placeholder: string,
		public apiNameCode: string,
		public apiNameTranslation: string,
		public requestApi: FilterApiRequest,
		protected override values: { code: string | undefined; translation: string | undefined }[] = [],
		public extraFields?: string,
		public noValueLabel?: string,
	) {
		super(filterId, label, values);
	}

	addEmptyValue() {
		super.addValue({ code: undefined, translation: undefined });
	}

	getFilters() {
		return { id: this.filterId, values: this.values.filter(value => value.code !== undefined) };
	}

	getLuceneExpression() {
		let result: LuceneExpression | undefined;
		for (const filterValue of this.values) {
			if (filterValue.translation === undefined && filterValue.code === undefined) {
				continue;
			}
			result = result
				? {
					left: result,
					operator: 'OR',
					parenthesized: false,
					right: this.createFieldExpression(filterValue.translation, filterValue.code)
				}
				: this.createFieldExpression(filterValue.translation, filterValue.code);
		}
		return result;
	}

	fromLuceneExpression(expression: LuceneExpression): {
		id: string;
		values: DatacenterCodeWithIdTranslationFilterType[];
	} {
		const targetFields = [this.apiNameTranslation, this.apiNameCode];
		const conditions = extractConditionsByFields(expression, targetFields);
		const valuesMap = new Map<string, DatacenterCodeWithIdTranslationFilterType>();
		for (const condition of conditions) {
			for (const [field, rawValue] of Object.entries(condition)) {
				const value = ensureString(rawValue);
				if (value === undefined) {
					continue;
				}
				const key = field === this.apiNameCode ? `code:${ value }` : `translation:${ value }`;
				const current = valuesMap.get(key) ?? { code: undefined, translation: undefined };
				if (field === this.apiNameCode) {
					current.code = value;
				} else {
					current.translation = value;
				}
				valuesMap.set(key, current);
			}
		}
		return { id: this.filterId, values: [...valuesMap.values()] };
	}

	private createFieldExpression(translation?: string, code?: string) {
		if (code === '') {
			return {
				field: this.apiNameCode,
				term: '""',
				quoted: false
			};
		}
		if (code !== undefined) {
			return {
				field: this.apiNameCode,
				term: code,
				quoted: false
			};
		}
		return {
			field: this.apiNameCode || this.apiNameTranslation,
			term: this.apiNameCode ? '' : `*${ term.escape(translation ?? '') }*`,
			quoted: false
		};
	}
}

export class OrderNumberFilter extends DatacenterViewFilter<DatacenterOrderNumberFilterType> {
	readonly filterType = 'OrderNumber';

	constructor(
		public override filterId: string,
		public override label: string,
		public placeholderReference: string,
		public placeholderItem: string,
		public apiReferenceName: string,
		public apiItemName: string,
		protected override values: DatacenterOrderNumberFilterType[] = []
	) {
		super(filterId, label, values);
	}

	addEmptyValue() {
		super.addValue({ reference: undefined, item: undefined });
	}

	getFilters() {
		return { id: this.filterId, values: this.values.filter(value => value.reference || value.item) };
	}

	getLuceneExpression() {
		let result: LuceneExpression | undefined;
		for (const filterValue of this.values.filter(value => value.item || value.reference)) {
			const expression = this.createExpression(filterValue);
			result = result && expression
				? {
					left: result,
					operator: 'OR',
					parenthesized: false,
					right: expression
				}
				: expression;
		}
		return result;
	}

	fromLuceneExpression(expression: LuceneExpression): {
		id: string;
		values: DatacenterOrderNumberFilterType[];
	} {
		const targetFields = [this.apiReferenceName, this.apiItemName];
		const collected: DatacenterOrderNumberFilterType[] = [];
		const addCandidate = (referenceRaw?: string, itemRaw?: string) => {
			const referenceTrimmed = referenceRaw?.trim() ?? '';
			const itemTrimmed = itemRaw?.trim() ?? '';
			let reference = referenceTrimmed || undefined;
			let item = itemTrimmed || undefined;
			if (referenceRaw) {
				const parsed = parseOrderNumberValue(referenceRaw);
				if (referenceRaw.includes(' - ')) {
					reference = parsed.reference ?? reference;
					item = parsed.item ?? item;
				} else {
					reference = parsed.reference ?? reference;
				}
			}
			if (itemRaw) {
				const parsed = parseOrderNumberValue(itemRaw);
				item = parsed.item ?? item;
				if (!reference) {
					reference = parsed.reference ?? reference;
				}
			}
			if (!reference && referenceTrimmed) {
				reference = referenceTrimmed;
			}
			if (!item && itemTrimmed) {
				item = itemTrimmed;
			}
			if (reference || item) {
				collected.push({ reference, item });
			}
		};
		for (const condition of extractCombinedConditionsByFields(expression, targetFields)) {
			addCandidate(ensureString(condition[this.apiReferenceName]), ensureString(condition[this.apiItemName]));
		}
		if (collected.length === 0) {
			for (const condition of extractConditionsByFields(expression, targetFields)) {
				addCandidate(ensureString(condition[this.apiReferenceName]), ensureString(condition[this.apiItemName]));
			}
		}
		if (collected.length === 0) {
			const matches = collectFieldValues(expression, [this.apiReferenceName]).get(this.apiReferenceName) ?? [];
			for (const value of matches.filter(isNonEmptyString)) {
				addCandidate(value);
			}
		}
		if (collected.length === 0) {
			const rawTerm = ensureString((<{ term?: unknown }> expression).term);
			if (rawTerm) {
				const reference = new RegExp(`${ escapeRegExp(this.apiReferenceName) }:([^\\s)"']+)`, 'i')
					.exec(rawTerm)?.[1];
				const item = new RegExp(`${ escapeRegExp(this.apiItemName) }:([^\\s)"']+)`, 'i').exec(rawTerm)?.[1];
				if (reference || item) {
					addCandidate(reference, item);
				}
			}
		}
		const values = deduplicate(collected, item => `${ item.reference ?? '' }|${ item.item ?? '' }`);
		return { id: this.filterId, values };
	}

	private createExpression(filter: DatacenterOrderNumberFilterType) {
		let referenceExp: FieldExpression | undefined;
		if (filter.reference) {
			referenceExp = {
				field: this.apiReferenceName,
				term: this.isExactTextMatchingEnabled()
					? escapeTextPreservingWildcards(filter.reference.trim())
					: `*${ term.escape(filter.reference.trim()) }*`,
				quoted: false
			};
		}

		let itemExp: FieldExpression | undefined;
		if (filter.item) {
			itemExp = {
				field: this.apiItemName,
				term: filter.item.trim(),
				quoted: false
			};
		}

		if (referenceExp && itemExp) {
			return {
				left: referenceExp,
				operator: 'AND',
				parenthesized: true,
				right: itemExp
			};
		}
		if (referenceExp) {
			return referenceExp;
		}

		return itemExp;
	}
}

export class DocumentOrderNumberFilter extends DatacenterViewFilter<DatacenterDocumentOrderNumberFilterType> {
	readonly filterType = 'DocumentOrderNumber';

	constructor(
		public override filterId: string,
		public override label: string,
		public placeholderReference: string,
		public placeholderItem: string,
		public apiReferenceName: string,
		public entityReferenceName: string,
		public apiItemName: string,
		protected override values: DatacenterDocumentOrderNumberFilterType[] = []
	) {
		super(filterId, label, values);
	}

	addEmptyValue() {
		super.addValue({ reference: undefined, item: undefined, entityReferenceName: this.entityReferenceName });
	}

	getFilters() {
		return { id: this.filterId, values: this.values.filter(value => value.reference || value.item) };
	}

	getLuceneExpression() {
		let result: LuceneExpression | undefined;
		for (const filterValue of this.values.filter(value => value.item || value.reference)) {
			const documentExp = this.createExpression(filterValue);
			result = result && documentExp
				? {
					left: result,
					operator: 'OR',
					parenthesized: false,
					right: documentExp
				}
				: documentExp;
		}
		return result;
	}

	fromLuceneExpression(expression: LuceneExpression): {
		id: string;
		values: DatacenterDocumentOrderNumberFilterType[];
	} {
		const entityField = `${ this.entityReferenceName }.${ this.apiReferenceName }`;
		const targetFields = [this.apiReferenceName, this.apiItemName, entityField];
		const conditions = extractConditionsByFields(expression, targetFields);
		// Deduplicate by (reference,item) and prefer the entity-field variant if both exist.
		const values = new Map<string, DatacenterDocumentOrderNumberFilterType>();
		for (const condition of conditions) {
			const documentReference = ensureString(condition[this.apiReferenceName]);
			const documentLineReference = ensureString(condition[entityField]);
			const itemReference = ensureString(condition[this.apiItemName]);
			const reference = documentReference ?? documentLineReference;
			if (!reference && !itemReference) {
				continue;
			}
			const key = `${ reference ?? '' }|${ itemReference ?? '' }`;
			const existing = values.get(key);
			if (!existing) {
				values.set(key, {
					reference: reference ?? undefined,
					item: itemReference ?? undefined,
					entityReferenceName: documentLineReference ? this.entityReferenceName : undefined
				});
				continue;
			}
			// If we ever see the entity-field variant, upgrade the existing record.
			if (documentLineReference && !existing.entityReferenceName) {
				existing.entityReferenceName = this.entityReferenceName;
			}
		}
		return { id: this.filterId, values: [...values.values()] };
	}

	private createExpression(filter: DatacenterDocumentOrderNumberFilterType) {
		let result: LuceneExpression | undefined;
		let documentReferenceExp: FieldExpression | undefined;
		let documentLineReferenceExp: FieldExpression | undefined;
		if (filter.reference) {
			documentReferenceExp = {
				field: this.apiReferenceName,
				term: this.isExactTextMatchingEnabled()
					? escapeTextPreservingWildcards(filter.reference.trim())
					: `*${ term.escape(filter.reference.trim()) }*`,
				quoted: false
			};

			if (filter.entityReferenceName) {
				documentLineReferenceExp = {
					field: `${ this.entityReferenceName }.${ this.apiReferenceName }`,
					term: this.isExactTextMatchingEnabled()
						? escapeTextPreservingWildcards(filter.reference.trim())
						: `*${ term.escape(filter.reference.trim()) }*`,
					quoted: false
				};
			}
		}

		let itemExp: FieldExpression | undefined;
		if (filter.item) {
			itemExp = {
				field: this.apiItemName,
				term: filter.item.trim(),
				quoted: false
			};
		}

		if (itemExp) {
			let documentLuceneExp: LuceneExpression | undefined;
			if (documentReferenceExp) {
				documentLuceneExp = {
					left: documentReferenceExp,
					operator: 'AND',
					parenthesized: true,
					right: itemExp
				};
			}

			let documentLineLuceneExp: LuceneExpression | undefined;
			if (documentLineReferenceExp) {
				documentLineLuceneExp = {
					left: documentLineReferenceExp,
					operator: 'AND',
					parenthesized: true,
					right: itemExp
				};
			}

			if (documentLuceneExp && documentLineLuceneExp) {
				return {
					left: documentLuceneExp,
					operator: 'OR',
					parenthesized: false,
					right: documentLineLuceneExp
				};
			}

			if (documentLuceneExp) {
				return documentLuceneExp;
			}
		} else {
			if (documentReferenceExp && documentLineReferenceExp) {
				return {
					left: documentReferenceExp,
					operator: 'OR',
					parenthesized: false,
					right: documentLineReferenceExp
				};
			}

			if (documentReferenceExp) {
				return documentReferenceExp;
			}
		}
		return result;
	}
}

export class DropdownFilter extends DatacenterViewFilter<DatacenterCodeTranslationFilterType> {
	readonly filterType = 'Dropdown';

	constructor(
		public override filterId: string,
		public override label: string,
		public placeholder: string,
		public apiNameCode: string,
		public apiNameTranslation: string,
		public requestApi: FilterApiRequest,
		protected override values: { code?: string; translation?: string }[] = [],
		protected createCustomFieldExpression:
			((translation?: string, code?: string) => LuceneExpression) | undefined = undefined,
		protected multiFilterCodes: Map<string, string[]> | undefined = undefined,
		protected applyLikeCondition = false,
		protected subObject?: string,
		protected shouldApplyNotNullCondition = false,
		public removeNoValueFilter = false
	) {
		super(filterId, label, values);
	}

	addEmptyValue() {
		super.addValue({ code: undefined, translation: undefined });
	}

	getFilters() {
		return {
			id: this.filterId,
			values: this.values.filter(value => Boolean(value.translation) || Boolean(value.code))
		};
	}

	getLuceneExpression() {
		let result: LuceneExpression | undefined;
		for (const filterValue of this.values.filter(value => Boolean(value.translation) || Boolean(value.code))) {
			if (!filterValue.code) {
				continue;
			}
			if (this.multiFilterCodes?.has(filterValue.code)) {
				const mapValue = this.multiFilterCodes.get(filterValue.code);
				if (mapValue) {
					for (const value of mapValue) {
						result = result
							? {
								left: result,
								operator: 'OR',
								parenthesized: false,
								right: this.createFieldExpression(filterValue.translation, value)
							}
							: this.createFieldExpression(filterValue.translation, value);
					}
				}
			} else {
				result = result
					? {
						left: result,
						operator: 'OR',
						parenthesized: false,
						right: this.createFieldExpression(filterValue.translation, filterValue.code)
					}
					: this.createFieldExpression(filterValue.translation, filterValue.code);
			}
		}
		return result;
	}

	fromLuceneExpression(expression: LuceneExpression): {
		id: string;
		values: DatacenterCodeTranslationFilterType[];
	} {
		const targetFields = [this.apiNameTranslation];
		if (this.apiNameCode) {
			targetFields.push(this.apiNameCode);
		}
		if (this.subObject) {
			targetFields.push(this.subObject);
		}
		const conditions = extractConditionsByFields(expression, targetFields);
		const values = new Map<string, DatacenterCodeTranslationFilterType>();
		for (const condition of conditions) {
			for (const [field, rawValue] of Object.entries(condition)) {
				const text = ensureString(rawValue);
				if (!text) {
					continue;
				}
				const isCodeField = field === this.apiNameCode || field === this.subObject;
				const key = isCodeField ? `code:${ text }` : `translation:${ text }`;
				const current = values.get(key) ?? { code: undefined, translation: undefined };
				if (isCodeField) {
					current.code = text;
				} else {
					current.translation = text;
				}
				values.set(key, current);
			}
		}
		return { id: this.filterId, values: [...values.values()] };
	}

	private createFieldExpression(translation?: string, code?: string): LuceneExpression | NodeExpression {
		if (code && code === 'novalue') {
			const notNullCondition = {
				field: `(!${ this.subObject ?? this.apiNameCode }`,
				term: '*)',
				quoted: false
			};
			const emptyCondition = {
				field: `(${ this.subObject ?? this.apiNameCode }`,
				term: '"")',
				quoted: false
			};
			return this.shouldApplyNotNullCondition ? {
				left: notNullCondition,
				operator: 'OR',
				right: emptyCondition,
				parenthesized: false,
			} : emptyCondition;
		}
		if (this.createCustomFieldExpression) {
			return this.createCustomFieldExpression(translation, code);
		}
		const field = this.subObject ?? (code ? this.apiNameCode : this.apiNameTranslation);
		const termPrefix = this.applyLikeCondition ? '*' : '"';
		const termSuffix = this.applyLikeCondition ? '*' : '"';
		const value = code ?? translation ?? '';

		return {
			field,
			term: this.isExactTextMatchingEnabled()
				? escapeTextPreservingWildcards(value)
				: `${ termPrefix }${ term.escape(value) }${ termSuffix }`,
			quoted: false
		};
	}
}

// document mill dropdown
export class DocumentMillDropdownFilter extends DatacenterViewFilter<DatacenterDocumentCodeTranslationFilterType> {
	readonly filterType = 'DocumentMillDropdown';

	constructor(
		public override filterId: string,
		public override label: string,
		public placeholder: string,
		public apiNameCode: string,
		public apiNameTranslation: string,
		public entityName: string | undefined,
		public requestApi: FilterApiRequest,
		protected override values: { code: string; translation?: string; entityName?: string }[] = []
	) {
		super(filterId, label, values);
	}

	addEmptyValue() {
		super.addValue({ code: undefined, translation: undefined, entityName: this.entityName });
	}

	getFilters() {
		return {
			id: this.filterId,
			values: this.values.filter(value => Boolean(value.translation) || value.code.length > 0)
		};
	}

	getLuceneExpression() {
		let result: LuceneExpression | undefined;
		for (const filterValue of this.values.filter(value => Boolean(value.translation) || Boolean(value.code))) {
			if (!filterValue.translation) {
				continue;
			}
			const expression = this.createFieldExpression(
				filterValue.translation,
				filterValue.code,
				filterValue.entityName
			);
			result = result
				? {
					left: result,
					operator: 'OR',
					parenthesized: false,
					right: expression
				}
				: expression;
		}
		return result;
	}

	fromLuceneExpression(expression: LuceneExpression): {
		id: string;
		values: DatacenterDocumentCodeTranslationFilterType[];
	} {
		const values = <DatacenterDocumentCodeTranslationFilterType[]> parseDocumentDropdownValues(
			expression,
			this.apiNameCode,
			this.apiNameTranslation,
			this.entityName ? `${ this.entityName }.${ this.apiNameCode }` : undefined,
			this.entityName
		);
		const normalized = this.entityName
			? values.map((value: DatacenterDocumentCodeTranslationFilterType) =>
				value.entityName ? value : { ...value, entityName: this.entityName })
			: values;
		return { id: this.filterId, values: normalized };
	}

	private createFieldExpression(translation: string, codes: string, entityName?: string): LuceneExpression {
		const groupedExpressions: LuceneExpression[] = [];
		// Split the codes string by ' || ' and trim each code
		const codeList = codes.split('||').map(code => code.trim()).filter(Boolean);

		for (const code of codeList) {
			const fieldExpressions: LuceneExpression[] = [];
			// Add entity field expression if applicable
			if (entityName && this.entityName) {
				fieldExpressions.push({
					field: `${ this.entityName }.${ this.apiNameCode }`,
					term: `"${ term.escape(code) }"`,
					quoted: false
				});
			}
			// Add base field expression
			fieldExpressions.push({ field: this.apiNameCode, term: `"${ term.escape(code) }"`, quoted: false });

			const [firstField, ...restFields] = fieldExpressions;
			let group: LuceneExpression = firstField;

			for (const field of restFields) {
				group = {
					left: group,
					operator: 'OR',
					right: field,
					parenthesized: true
				};
			}

			groupedExpressions.push(group);
		}

		// If no codes, fallback to translation
		if (groupedExpressions.length === 0) {
			return {
				field: this.apiNameTranslation,
				term: `"${ term.escape(translation) }"`,
				quoted: false
			};
		}

		const [firstGroup, ...restGroups] = groupedExpressions;
		let combined: LuceneExpression = firstGroup;

		for (const group of restGroups) {
			combined = {
				left: combined,
				operator: 'OR',
				right: group,
				parenthesized: true
			};
		}
		return combined;
	}
}

export class DatacenterBooleanFilter extends DatacenterViewFilter<DatacenterBooleanFilterType[]> {
	readonly filterType = 'Boolean';

	constructor(
		filterId: string,
		label: string,
		buttonLabels: string[],
		apiNames: string[]
	);
	constructor(
		filterId: string,
		label: string,
		buttonLabels: string[],
		apiNames: [string],
		fixedValues: string[],
		values?: DatacenterBooleanFilterType[][]
	);
	constructor(
		public override filterId: string,
		public override label: string,
		public buttonLabels: string[],
		public apiNames: string[],
		public fixedValues?: string[],
		protected override values: DatacenterBooleanFilterType[][] = []
	) {
		super(filterId, label, values);
	}

	addEmptyValue() {
		const arrayOfValues = [];
		for (let i = 0; i < this.buttonLabels.length; i++) {
			if (this.fixedValues) {
				arrayOfValues.push({ apiName: this.apiNames[0], selected: false, value: this.fixedValues[i] });
			} else {
				arrayOfValues.push({ apiName: this.apiNames[i], selected: false });
			}
		}
		super.addValue(arrayOfValues);
	}

	getFilters() {
		return { id: this.filterId, values: this.values.filter(value => value.find(element => element.selected)) };
	}

	getLuceneExpression() {
		let result: LuceneExpression | undefined;
		if (this.isValidDatacenterCodeTranslationFilterTypeArray(<DatacenterCodeTranslationFilterType[]> this.values)) {
			const arrayOfValues = [];
			const filteredValues = this.convertValues(<DatacenterCodeTranslationFilterType[]> this.values);
			for (let i = 0; i < this.buttonLabels.length; i++) {
				if (this.fixedValues) {
					arrayOfValues.push({ apiName: this.apiNames[0], selected: false, value: this.fixedValues[i] });
				}
			}
			for (const item of arrayOfValues) {
				const [firstFilteredValue] = filteredValues;
				if (Array.isArray(firstFilteredValue)) {
					const selectedItem = firstFilteredValue.find(filteredVal => filteredVal.value === item.value);
					item.selected = selectedItem?.selected ?? false;
				} else {
					item.selected = false;
				}
			}
			this.addValues([arrayOfValues]);
		}
		for (const filterValue of this.values.filter(value => value.find(element => element.selected))) {
			const right = this.createNodeExpression(filterValue);
			result = result
				? right
					? {
						left: result,
						operator: 'OR',
						parenthesized: false,
						right
					}
					: result
				: right;
		}

		return result;
	}

	fromLuceneExpression(expression: LuceneExpression): {
		id: string;
		values: DatacenterBooleanFilterType[][];
	} {
		const selections = parseBooleanFilterSelections(expression, {
			buttonLabels: this.buttonLabels,
			apiNames: this.apiNames,
			fixedValues: this.fixedValues,
			filterId: this.filterId
		});
		return { id: this.filterId, values: selections };
	}

	isValidDatacenterCodeTranslationFilterTypeArray(
		arr: DatacenterCodeTranslationFilterType[]
	) {
		return arr.every(item =>
			typeof item === 'object'
			&& Object.hasOwn(item, 'code') && Object.hasOwn(item, 'translation'));
	}

	convertValues(filterValues: DatacenterCodeTranslationFilterType[]) {
		return [
			filterValues.map(item => ({
				apiName: this.apiNames[0],
				value: item.code,
				selected: true
			}))
		];
	}

	private createNodeExpression(filterBooleanValue: DatacenterBooleanFilterType[]) {
		let result: LuceneExpression | undefined;

		for (const filterValueElement of filterBooleanValue.filter(filterBoolean => filterBoolean.selected)) {
			result = result
				? {
					left: result,
					operator: 'OR',
					parenthesized: false,
					right: this.fixedValues ? this.createFieldExpression(filterValueElement, filterBooleanValue
						.indexOf(filterValueElement))
						: this.createRangeExpression(filterValueElement)
				}
				: this.fixedValues ? this.createFieldExpression(filterValueElement, filterBooleanValue
					.indexOf(filterValueElement))
					: this.createRangeExpression(filterValueElement);
		}
		return result;
	}

	private createRangeExpression(filterValue: DatacenterBooleanFilterType) {
		return <const> {
			field: filterValue.apiName,
			term_min: '0',
			term_max: '*',
			inclusive: 'right'
		};
	}

	private createFieldExpression(filterValue: DatacenterBooleanFilterType, index: number) {
		return {
			field: this.filterId === 'bocRole' ? `${ filterValue.apiName }.${ this.buttonLabels[index] }`
				: filterValue.apiName,
			term: safe(filterValue.value),
			quoted: true
		};
	}
}

export class ShipmentOrderNumberFilter extends DatacenterViewFilter<DatacenterOrderNumberFilterType> {
	readonly filterType = 'ShipmentOrderNumber';

	constructor(
		public override filterId: string,
		public override label: string,
		public placeholderReference: string,
		public placeholderItem: string,
		public apiReferenceName: string,
		protected override values: DatacenterOrderNumberFilterType[] = []
	) {
		super(filterId, label, values);
	}

	addEmptyValue() {
		super.addValue({ reference: undefined, item: undefined });
	}

	getFilters() {
		return { id: this.filterId, values: this.values.filter(value => value.reference || value.item) };
	}

	getLuceneExpression() {
		let result: LuceneExpression | undefined;
		for (const filterValue of this.values.filter(value => value.item || value.reference)) {
			const expression = this.createExpression(filterValue);
			result = result
				? {
					left: result,
					operator: 'OR',
					parenthesized: false,
					right: expression
				}
				: expression;
		}
		return result;
	}

	fromLuceneExpression(expression: LuceneExpression): {
		id: string;
		values: DatacenterOrderNumberFilterType[];
	} {
		return { id: this.filterId, values: parseShipmentOrderNumbers(expression, this.apiReferenceName) };
	}

	private createExpression(filter: DatacenterOrderNumberFilterType) {
		const fieldValue = filter.reference && filter.item
			? filter.reference.trim().concat(' - ').concat(filter.item.trim().padStart(6, '0'))
			: filter.reference && !filter.item ? filter.reference.trim()
				: !filter.reference && filter.item ? filter.item.trim().padStart(6, '0') : '';
		return {
			field: this.apiReferenceName,
			term: this.isExactTextMatchingEnabled()
				? escapeTextPreservingWildcards(fieldValue) : term.escape(fieldValue),
			quoted: false
		};
	}
}

export class DatacenterNumericFilter extends DatacenterViewFilter<DatacenterDateNumericType> {
	readonly filterType = 'Numeric';

	constructor(
		public override filterId: string,
		public override label: string,
		public placeholderFrom: string,
		public placeholderTo: string,
		public apiName: string,
		protected override values: DatacenterDateNumericType[] = []
	) {
		super(filterId, label, values);
	}

	addEmptyValue() {
		super.addValue({ from: '', to: '' });
	}

	getFilters() {
		return {
			id: this.filterId,
			values: this.values.filter(value => (value.to || value.from) && this.isValidFilter(value.from, value.to))
		};
	}

	getLuceneExpression() {
		let result: FieldExpression | NodeExpression | RangeExpression | undefined;
		for (const filterValue of
			this.values.filter(value => (value.to || value.from) && this.isValidFilter(value.from, value.to))) {
			result = result
				? {
					left: result,
					operator: 'OR',
					parenthesized: false,
					right: this.createRangeExpression(filterValue)
				}
				: this.createRangeExpression(filterValue);
		}
		if (!result) {
			return undefined;
		}
		if (this.apiName === 'producedLength') {
			const formClause = { field: 'form', term: '04', quoted: false };
			result = {
				left: formClause,
				operator: 'AND',
				parenthesized: false,
				right: result
			};
		}
		return result;
	}

	fromLuceneExpression(expression: LuceneExpression): {
		id: string;
		values: DatacenterDateNumericType[];
	} {
		const values = parseNumericRanges(expression, this.apiName);
		return { id: this.filterId, values };
	}

	isValidFilter(from: string, to: string) {
		const isValidValues = this.isValidValue(from) && this.isValidValue(to);
		let isValidCriterium = true;
		if (from && to) {
			isValidCriterium = Number.parseFloat(from) <= Number.parseFloat(to);
		}
		return isValidValues && isValidCriterium;
	}

	private applyProducedLengthScaling(raw: string): string {
		if (this.filterId !== 'producedLength') {
			return raw;
		}
		const n = Number.parseFloat(raw.trim());
		if (!Number.isFinite(n)) {
			return raw;
		}
		return String(n * 1000);
	}

	private createRangeExpression(filterValue: DatacenterDateNumericType) {
		const from = filterValue.from ? this.applyProducedLengthScaling(filterValue.from) : '';
		const to = filterValue.to ? this.applyProducedLengthScaling(filterValue.to) : '';

		return <const> {
			field: this.apiName,
			term_min: from || '*',
			term_max: to || '*',
			inclusive: 'both'
		};
	}

	private isValidValue(value: string) {
		const pattern = /^[\d .]*$/;
		return pattern.test(value);
	}
}

export class FilterApiRequest {
	readonly url: string;
	distinct = true;
	limit = 5;

	constructor(
		url: string,
		params?: FilterApiParams
	) {
		this.url = url;
		if (params) {
			this.distinct = params.distinct !== undefined ? params.distinct : this.distinct;
			this.limit = params.limit !== undefined ? params.limit : this.limit;
		}
	}
}

interface FilterApiParams {
	distinct?: boolean;
	limit?: number;
}

export interface DynamicDatacenterFilter<T extends FilterType> {
	filter?: T;
	removeFilter: EventEmitter<void>;
	blockFilter?: EventEmitter<string>;
	getPreparedFilterValues(): { id: string; values: unknown[] } | undefined;
}

export class DocumentLastConnectionDateFilter extends DatacenterViewFilter<DocumentLastConnectionDateFilterType[]> {
	readonly filterType = 'DocumentLastConnectionDate';

	constructor(
		filterId: string,
		label: string,
		apiNames: string,
		fixedValue?: string,
		values?: DocumentLastConnectionDateFilterType[][]
	);
	constructor(
		public override filterId: string,
		public override label: string,
		public apiNames: string,
		public fixedValue?: string,
		protected override values: DocumentLastConnectionDateFilterType[][] = []
	) {
		super(filterId, label, values);
	}

	addEmptyValue() {
		const arrayOfValues = [];
		if (this.fixedValue) {
			arrayOfValues.push({ apiName: this.apiNames, selected: false, value: this.fixedValue });
		}
		super.addValue(arrayOfValues);
	}

	getFilters() {
		return { id: this.filterId, values: this.values.filter(value => value.find(element => element.selected)) };
	}

	getLuceneExpression() {
		let result: LuceneExpression | undefined;
		for (const filterValue of this.values.filter(value => value.find(element => element.selected))) {
			const right = this.createNodeExpression(filterValue);
			result = result
				? right
					? {
						left: result,
						operator: 'OR',
						parenthesized: false,
						right
					}
					: result
				: right;
		}

		return result;
	}

	fromLuceneExpression(expression: LuceneExpression): {
		id: string;
		values: DocumentLastConnectionDateFilterType[][];
	} {
		const values = parseDocumentLastConnectionDateSelections(expression, this.apiNames, this.fixedValue);
		return { id: this.filterId, values };
	}

	private createNodeExpression(filterBooleanValue: DocumentLastConnectionDateFilterType[]) {
		let result: LuceneExpression | undefined;

		for (const filterValueElement of filterBooleanValue.filter(filterBoolean => filterBoolean.selected)) {
			result = result
				? {
					left: result,
					operator: 'OR',
					parenthesized: false,
					right: this.createRangeExpression(filterValueElement)
				}
				: this.createRangeExpression(filterValueElement);
		}
		return result;
	}

	private createRangeExpression(filterValue: DocumentLastConnectionDateFilterType) {
		return <const> {
			field: filterValue.apiName,
			term_min: filterValue.value || new Date().toISOString(),
			term_max: '*',
			inclusive: 'none'
		};
	}
}

export class DocumentSuggestiveListFilterWithId extends DatacenterViewFilter<DocumentCodeWithIdFilterType> {
	readonly filterType = 'DocumentSuggestiveListWithId';

	constructor(
		public override filterId: string,
		public override label: string,
		public placeholder: string,
		public apiCode: string,
		public apiNameCode: string,
		public entityName: string | undefined,
		public requestApi: FilterApiRequest,
		protected override values: {
			id: string | undefined;
			code: string | undefined;
			entityName?: string | undefined;
		}[] = []

	) {
		super(filterId, label, values);
	}

	addEmptyValue() {
		super.addValue({ id: undefined, code: undefined, entityName: this.entityName });
	}

	getFilters() {
		return { id: this.filterId, values: this.values.filter(value => Boolean(value.id)) };
	}

	getLuceneExpression() {
		let result: LuceneExpression | undefined;
		for (const filterValue of this.values.filter(value => Boolean(value.id))) {
			if (!filterValue.id) {
				continue;
			}
			const documentExp = this.createFieldExpression(filterValue.entityName, filterValue.id);
			result = result && documentExp
				? {
					left: result,
					operator: 'OR',
					parenthesized: false,
					right: documentExp
				}
				: documentExp;
		}
		return result;
	}

	fromLuceneExpression(expression: LuceneExpression): {
		id: string;
		values: DocumentCodeWithIdFilterType[];
	} {
		const entityFields: string[] = [];
		if (this.entityName) {
			const field = this.entityName.includes('.') ? this.entityName : `${ this.entityName }.${ this.apiCode }`;
			entityFields.push(field);
		}
		const values = parseDocumentSuggestiveListIds(expression, this.apiCode, entityFields, this.entityName);
		return { id: this.filterId, values };
	}

	private createFieldExpression(entityName?: string, apiCode?: string) {
		let result: LuceneExpression | undefined;
		let documentReferenceExp: FieldExpression | undefined;
		let documentLineReferenceExp: FieldExpression | undefined;
		if (apiCode) {
			documentReferenceExp = {
				field: this.apiCode,
				term: term.escape(apiCode),
				quoted: false
			};

			if (this.entityName?.includes('.')) {
				return {
					field: this.entityName,
					term: term.escape(apiCode),
					quoted: false
				};
			} else if (entityName && this.entityName) {
				documentLineReferenceExp = {
					field: `${ this.entityName }.${ this.apiCode }`,
					term: term.escape(apiCode),
					quoted: false
				};
			}

			if (documentLineReferenceExp) {
				return {
					left: documentReferenceExp,
					operator: 'OR',
					parenthesized: false,
					right: documentLineReferenceExp
				};
			}
			return documentReferenceExp;
		}
		return result;
	}
}
